import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MdDialog, MdDialogRef } from '@angular/material';
import { Router } from '@angular/router';
import { FeatureType, Language } from 'app/model/constant';
import { DestinationTravelPolicy } from 'app/model/products/hotel/hotel.model';
import { TranslatePipe } from 'app/pipes/translate.pipe';
import { AppConfigService } from 'app/services/app-config/app-config.service';
import { AuthorizeService } from 'app/services/authorize/authorize.service';
import { UserService } from 'app/services/user/user.service';
import { UtilitiesService } from 'app/services/utilities/utilities.service';
import * as moment from 'vendors/moment-locale-th';
import { DialogConfirmComponent } from '../modal/dialog-confirm/dialog-confirm.component';
import { DialogConfirmType } from '../modal/dialog-confirm/dialog-confirm.model';
import { DialogShareBudgetComponent } from '../modal/dialog-share-budget/dialog-share-budget.component';
import { Booker, ShareBudget } from '../modal/dialog-share-budget/dialog-share-budget.model';
import { BUDGET_TYPE, Item, TEMPLATE } from './budget-limit.model';
import { DialogBudgetLimitDetailComponent } from './dialog-budget-limit/dialog-budget-limit-detail/dialog-budget-limit-detail.component';
import { DialogBudgetLimitMobileComponent } from './dialog-budget-limit/dialog-budget-limit-mobile/dialog-budget-limit-mobile.component';

@Component({
  selector: 'act-budget-limit',
  templateUrl: './budget-limit.component.html',
  styleUrls: ['./budget-limit.component.scss']
})

export class BudgetLimitComponent implements OnChanges {

  @Input() name: string;
  @Input() destinationTravelPolicyBooker: DestinationTravelPolicy[];
  @Input() destinationTravelPolicyCoBooker: DestinationTravelPolicy[];
  @Input() normalBudgetBooker: number;
  @Input() normalBudgetBookerUnlimited: boolean;
  @Input() normalBudgetCoBooker: number;
  @Input() normalBudgetCoBookerUnlimited: boolean;
  @Input() normalCoBudget?: number;
  @Input() normalCoBudgetUnlimited?: boolean;
  @Input() allowLimitBudget: boolean = true;
  @Input() isPOI: boolean = false;
  @Input() isWorkPurpose: boolean = true;
  @Input() allowBookForTraveller: boolean;
  @Input() isLoading: boolean = true;

  @Output() onShareBudget: EventEmitter<any> = new EventEmitter();

  public lang;
  public description: string;
  public template: string;
  public itemList = [];
  public itemListCoBooker = [];
  public policyIdList = new Set();
  public policyIdListCoBooker = new Set();
  public textAverageBudget: string;
  public textNormalBudget: string;
  public isLimitBudget: boolean = false;
  public isNewBudget: boolean = false;
  public isShareBudget: boolean = false;
  public shareBudget: ShareBudget;
  public appliedCoBudget: boolean = false;
  public showBudgetBar: boolean = false;

  constructor(
    private utilities: UtilitiesService,
    private translate: TranslatePipe,
    private decimal: DecimalPipe,
    private dialog: MdDialog,
    private appConfigService: AppConfigService,
    private userService: UserService,
    private router: Router,
    private authorizeService: AuthorizeService
  ) {
    const featureShareBudget = this.userService.getUserFeature(FeatureType.ALLOW_SHARE_BUDGET, true);
    this.isShareBudget = featureShareBudget && featureShareBudget.allow;
    this.lang = this.appConfigService.getLanguage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.lang = this.appConfigService.getLanguage();
    this.policyIdList = new Set();
    this.policyIdListCoBooker = new Set();
    this.itemList = [];
    this.itemListCoBooker = [];

    this.isShareBudget && this.getShareBudget();
    if (this.shareBudget) {
      this.selectTemplateShareBudget();
    } else {
      if (this.allowLimitBudget && this.destinationTravelPolicyBooker || this.isShareBudget) {
        this.selectTemplate();
      } else {
        this.isLimitBudget = false;
      }
    }

    if (changes['isLoading']) {
      if (changes['isLoading'].previousValue === true && changes['isLoading'].currentValue === false) {
        this.showBudgetBar = true;
      } else {
        this.showBudgetBar = false;
      }
    }
  }

  selectTemplate() {
    this.template = '';
    this.textNormalBudget = this.normalBudgetBooker ? this.transformBudget(this.normalBudgetBooker) : `${this.translate.transform('budget-limit-unlimited')}`;
    this.isNewBudget = this.checkNewBudget();
    this.isLimitBudget = this.destinationTravelPolicyBooker.some(f => !!f.averageBudget);

    switch (true) {
      case this.isLimitBudget:
        if (this.isPOI) {
          this.template = TEMPLATE.SPECIAL_BUDGET_POI;
        } else if (this.policyIdList.size > 1) {
          this.template = TEMPLATE.SPECIAL_BUDGET_SOME_DAYS;
        } else {
          this.template = TEMPLATE.SPECIAL_BUDGET;
        }
        break;
      case this.destinationTravelPolicyBooker.length === 0 && !!this.normalBudgetBooker:
        this.template = TEMPLATE.NORMAL_BUDGET;
        break;
      default:
        if (this.policyIdList.size >= 1 && this.isShareBudget) {
          this.template = TEMPLATE.UNLIMITED_SHARE_BUDGET_FEATURE;
        } else {
          this.template = TEMPLATE.UNLIMITED_BUDGET;
        }
    }
    this.transform();
  }

  selectTemplateShareBudget() {
    this.template = '';
    const sumNormalBudget = this.normalBudgetBooker + this.normalBudgetCoBooker;
    if (this.normalCoBudgetUnlimited === undefined) {
      this.textNormalBudget = sumNormalBudget
        ? this.transformBudget(sumNormalBudget)
        : this.translate.transform('budget-limit-unlimited');
    } else if (this.normalCoBudgetUnlimited && (this.normalBudgetBookerUnlimited || this.normalBudgetCoBookerUnlimited)) {
      this.textNormalBudget = this.translate.transform('budget-limit-unlimited');
    } else {
      if (sumNormalBudget) {
        this.textNormalBudget = this.transformBudget(sumNormalBudget < this.normalCoBudget ? sumNormalBudget : this.normalCoBudget);
      } else {
        this.textNormalBudget = this.translate.transform('budget-limit-unlimited');
      }
    }
    this.isNewBudget = true;

    this.destinationTravelPolicyBooker.forEach(d => {
      d.travelPolicyList && d.travelPolicyList.forEach(t => {
        this.policyIdList.add(t.policyId);
      });
    });

    this.destinationTravelPolicyCoBooker.forEach(d => {
      d.travelPolicyList && d.travelPolicyList.forEach(t => {
        this.policyIdListCoBooker.add(t.policyId);
      });
    });

    if ((this.destinationTravelPolicyBooker.length > 0 && this.destinationTravelPolicyBooker.some(d => !!d.travelPolicyList)) ||
      (this.destinationTravelPolicyCoBooker.length > 0 && this.destinationTravelPolicyCoBooker.some(d => !!d.travelPolicyList))) {
      if (this.isPOI) {
        this.template = TEMPLATE.SHARE_BUDGET_SPECIAL_BUDGET_POI;
      } else {
        this.template = TEMPLATE.SHARE_BUDGET_SPECIAL_BUDGET;
      }
    } else {
      this.template = TEMPLATE.SHARE_BUDGET;
    }

    this.transform();
  }

  transform() {
    switch (this.template) {
      case TEMPLATE.SPECIAL_BUDGET:
        this.transformTemplateSpecialBudget();
        this.isLimitBudget = true;
        break;
      case TEMPLATE.SPECIAL_BUDGET_SOME_DAYS:
        this.transformTemplateSpecialBudgetSomeDays();
        this.isLimitBudget = true;
        break;
      case TEMPLATE.SPECIAL_BUDGET_POI:
      case TEMPLATE.SHARE_BUDGET_SPECIAL_BUDGET_POI:
        this.transformTemplatePOI();
        this.isLimitBudget = true;
        break;
      case TEMPLATE.NORMAL_BUDGET:
        this.description = this.translate.transform(`product-detail-budget-limit`).replace(/{x}/gi, this.utilities.getPriceRangeFormatText(this.normalBudgetBooker));
        this.isLimitBudget = true;
        break;
      case TEMPLATE.UNLIMITED_BUDGET:
        this.description = this.translate.transform(`search-box-budget-unlimited`);
        this.isLimitBudget = false;
        this.isNewBudget = false;
        break;
      case TEMPLATE.UNLIMITED_SHARE_BUDGET_FEATURE:
        this.description = this.translate.transform(`search-box-budget-unlimited`);
        this.isLimitBudget = false;
        this.isNewBudget = true;
        break;
      case TEMPLATE.SHARE_BUDGET:
        let totalBudget;
        if (this.shareBudget.booker.amount && this.shareBudget.coBooker.amount) {
          totalBudget = this.shareBudget.booker.amount + this.shareBudget.coBooker.amount;
        }
        this.appliedCoBudget = this.destinationTravelPolicyBooker.length > 0 ? this.destinationTravelPolicyBooker.some(tp => tp.appliedCoBudget) : false;
        if (this.appliedCoBudget) {
          totalBudget = this.destinationTravelPolicyBooker[0].totalBudget;
        }
        if (totalBudget) {
          this.description = this.translate.transform(`budget-limit-share-budget`).replace(/{x}/gi, this.utilities.getPriceRangeFormatText(totalBudget));
        } else {
          this.description = this.translate.transform(`budget-limit-unlimited-share-budget`);
        }
        this.shareBudget.totalBudget = totalBudget;
        this.isLimitBudget = true;
        break;
      case TEMPLATE.SHARE_BUDGET_SPECIAL_BUDGET:
        this.transformTemplateShareBudgetSpecialBudget();
        this.isLimitBudget = true;
        break;
      default:
        this.isLimitBudget = false;
        break;
    }
  }

  transformTemplatePOI() {
    this.description = this.shareBudget ? this.translate.transform(`budget-limit-special-budget-for-poi-new`) : this.translate.transform(`budget-limit-special-budget-for-poi`);
    const roundDestination = this.destinationTravelPolicyBooker.length;
    for (let index = 0; index < roundDestination; index++) {
      if (this.destinationTravelPolicyBooker[index].appliedCoBudget || this.destinationTravelPolicyBooker[index].travelPolicyList) {
        const destination = `${this.translate.transform('budget-limit-for')}${this.lang === Language.en ? ` ${this.destinationTravelPolicyBooker[index].destinationNameEn}` : `${this.destinationTravelPolicyBooker[index].destinationNameTh}`}`;
        const budget = this.destinationTravelPolicyBooker[index].averageBudget ? this.transformBudget(this.destinationTravelPolicyBooker[index].averageBudget) : `${this.translate.transform('budget-limit-unlimited')}`;
        this.itemList.push(new Item(destination, budget));
      }
    }
    if (this.itemList.length === 0) {
      this.description = this.normalBudgetBooker ?
        this.translate.transform(`product-detail-budget-limit`).replace(/{x}/gi, this.utilities.getPriceRangeFormatText(this.normalBudgetBooker)) :
        this.translate.transform(`budget-limit-unlimited`);
    }

    const roundDestinationCoBooker = this.destinationTravelPolicyCoBooker.length
    for (let index = 0; index < roundDestinationCoBooker; index++) {
      if (this.destinationTravelPolicyCoBooker[index].appliedCoBudget || this.destinationTravelPolicyCoBooker[index].travelPolicyList) {
        const destination = `${this.translate.transform('budget-limit-for')}${this.lang === Language.en ? ` ${this.destinationTravelPolicyCoBooker[index].destinationNameEn}` : `${this.destinationTravelPolicyCoBooker[index].destinationNameTh}`}`;
        const budget = this.destinationTravelPolicyCoBooker[index].averageBudget ? this.transformBudget(this.destinationTravelPolicyCoBooker[index].averageBudget) : `${this.translate.transform('budget-limit-unlimited')}`;
        this.itemListCoBooker.push(new Item(destination, budget));
      }
    }
    this.appliedCoBudget = this.destinationTravelPolicyBooker.length > 0 ? this.destinationTravelPolicyBooker.some(tp => tp.appliedCoBudget) : false;
  }

  transformTemplateSpecialBudget() {
    const bookerBudget = this.destinationTravelPolicyBooker.length > 0 ? this.destinationTravelPolicyBooker[0].averageBudget : this.normalBudgetBooker;
    this.description = this.translate.transform(`product-detail-budget-limit`).replace(/{x}/gi, this.utilities.getPriceRangeFormatText(bookerBudget));
    this.textAverageBudget = this.transformBudget(this.destinationTravelPolicyBooker[0].averageBudget);
  }

  transformTemplateShareBudgetSpecialBudget() {
    const bookerBudget = this.destinationTravelPolicyBooker.length > 0 ? this.destinationTravelPolicyBooker[0].averageBudget : this.normalBudgetBooker;
    const coBookerBudget = this.destinationTravelPolicyCoBooker.length > 0 ? this.destinationTravelPolicyCoBooker[0].averageBudget : this.normalBudgetCoBooker;
    let totalBudget;
    if (bookerBudget && coBookerBudget) {
      totalBudget = bookerBudget + coBookerBudget;
    }
    this.appliedCoBudget = this.destinationTravelPolicyBooker.length > 0 ? this.destinationTravelPolicyBooker.some(tp => tp.appliedCoBudget) : false;
    if (this.appliedCoBudget) {
      totalBudget = this.destinationTravelPolicyBooker[0].totalBudget;
    }

    if (totalBudget) {
      this.description = this.translate.transform(`budget-limit-share-budget`).replace(/{x}/gi, this.utilities.getPriceRangeFormatText(totalBudget));
    } else {
      this.description = this.translate.transform(`budget-limit-unlimited-share-budget`);
    }

    this.textAverageBudget = this.transformBudget(totalBudget);

    this.shareBudget.booker.amount = bookerBudget;
    this.shareBudget.coBooker.amount = coBookerBudget;
    this.shareBudget.totalBudget = totalBudget;
  }

  transformTemplateSpecialBudgetSomeDays() {
    this.itemList = [];
    this.description = this.translate.transform(`product-detail-budget-limit`).replace(/{x}/gi, this.utilities.getPriceRangeFormatText(this.destinationTravelPolicyBooker[0].averageBudget));
    this.textAverageBudget = this.transformBudget(this.destinationTravelPolicyBooker[0].averageBudget);

    const policy = this.destinationTravelPolicyBooker[0];
    const mapDayBudget = new Map();
    policy.travelPolicyList.filter(f => f.budgetType === BUDGET_TYPE.NEW_BUDGET).map((p) => {
      const dayOfweek = moment(p.date, this.appConfigService.dateAPIFormat).format('E');
      mapDayBudget.set(dayOfweek, p.budget);
    });

    for (let r = 1; r <= 7; r++) {
      const budget = mapDayBudget.get('' + r);
      if (budget) {
        let tmpKeys = [];
        mapDayBudget.forEach((value, key) => {
          if (value === budget) {
            tmpKeys.push(key);
            mapDayBudget.delete(key);
          }
        });
        let dateString = '';
        tmpKeys.sort().map((m, i) => {
          if (i === 0) {
            dateString += `${this.translate.transform('budget-limit-for')}${this.lang === Language.en ? ' ' : ''}${this.convertDays(m, this.lang)}`;
          } else if (i === tmpKeys.length - 1) {
            dateString += `${this.lang === Language.en ? ' ' : ''}${this.translate.transform('budget-limit-and')}${this.lang === Language.en ? ' ' : ''}${this.convertDays(m, this.lang)}`;
          } else {
            dateString += `${this.lang === Language.en ? ', ' : ' '}${this.convertDays(m, this.lang)}`;
          }
        });
        this.itemList.push(new Item(dateString, this.transformBudget(budget)));
      }
    }
  }

  checkNewBudget() {
    let newBudget = false;
    this.destinationTravelPolicyBooker.forEach(d => {
      d.travelPolicyList && d.travelPolicyList.forEach(t => {
        if (t.budgetType === BUDGET_TYPE.NEW_BUDGET) {
          newBudget = true;
        }
        this.policyIdList.add(t.policyId);
      });
    });
    return newBudget;
  }

  getShareBudget() {
    const coBookerCookie = this.authorizeService.getCoBookerCookie();
    if (coBookerCookie) {
      this.shareBudget = new ShareBudget();
      this.shareBudget.booker = new Booker(this.userService.getUser().userId, this.userService.getUser().companyId, this.userService.getUser().email, this.normalBudgetBooker);
      this.shareBudget.coBooker = new Booker(coBookerCookie.userId, coBookerCookie.companyId, coBookerCookie.email, coBookerCookie.amount);
      this.shareBudget.totalBudget = this.shareBudget.booker.amount + this.shareBudget.coBooker.amount;
    } else {
      this.shareBudget = undefined;
      this.authorizeService.removeCoBookerCookie();
    }
  }

  onMobileClickDetail() {
    this.dialog.open(DialogBudgetLimitMobileComponent, {
      data: {
        budgetBooker: this.normalBudgetBooker,
        budgetCoBooker: this.normalBudgetCoBooker,
        destinationTravelPolicyBooker: this.destinationTravelPolicyBooker,
        destinationTravelPolicyCoBooker: this.destinationTravelPolicyCoBooker,
        itemList: this.itemList,
        itemListCoBooker: this.itemListCoBooker,
        template: this.template,
        textAverageBudget: this.textAverageBudget,
        shareBudget: this.shareBudget,
      }
    });
  }

  onClickMoreDetail() {
    this.dialog.open(DialogBudgetLimitDetailComponent, {
      data: {
        budgetBooker: this.normalBudgetBooker,
        budgetCoBooker: this.normalBudgetCoBooker,
        coBudget: this.normalCoBudget,
        coBudgetUnlimited: this.normalCoBudgetUnlimited,
        appliedCoBudget: this.appliedCoBudget,
        destinationTravelPolicyBooker: this.destinationTravelPolicyBooker,
        destinationTravelPolicyCoBooker: this.destinationTravelPolicyCoBooker,
        isShareBudget: !!this.shareBudget
      }
    });
  }

  transformBudget(budget: number) {
    return `THB ${this.decimal.transform(budget, '1.0')}`;
  }

  convertDays(day: string, lang: string) {
    return `${this.lang === Language.th ? 'วัน' : ''}${moment(day, 'E').locale(lang).format('dddd')}`;
  }

  openDialogShareBudget() {
    const dialogRef: MdDialogRef<DialogShareBudgetComponent> = this.dialog.open(DialogShareBudgetComponent);
    dialogRef.afterClosed().subscribe((val: { event: string, shareBudget: ShareBudget }) => {

      if (val.event === 'success') {
        this.shareBudget = val.shareBudget;
        this.shareBudget.booker = new Booker(this.userService.getUser().userId, this.userService.getUser().companyId, this.userService.getUser().email, this.normalBudgetBooker);
        this.shareBudget.totalBudget = this.shareBudget.booker.amount + this.shareBudget.coBooker.amount;
        this.onShareBudget.emit(new Booker(val.shareBudget.coBooker.userId, val.shareBudget.coBooker.companyId, val.shareBudget.coBooker.email, val.shareBudget.coBooker.amount));
        this.authorizeService.setShareBudgetObs(true);
        this.selectTemplate();
      }

      if (val.event === 'closeDialog') {
        dialogRef.close();
      }

      if (val.event === 'close') {
        dialogRef.close();
        const dialog = this.dialog.open(DialogConfirmComponent, { data: { confirmType: DialogConfirmType.CONFIRM_CLOSE } });
        dialog.afterClosed().subscribe(res => {
          if (!res) {
            this.openDialogShareBudget();
          }
        });
      }

      if (val.event === 'bookerNotAllowFeature') {
        // Reload current router
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.navigated = false;
        this.router.navigateByUrl(this.router.url);
      }
    });
  }

  onCancelShareBudget() {
    const message = { text: this.translate.transform('dialog-cancel-share-budget-text'), subText: this.translate.transform('dialog-cancel-share-budget-sub-text') };
    const dialogRef: MdDialogRef<DialogConfirmComponent> = this.dialog.open(DialogConfirmComponent,
      {
        data: {
          confirmType: DialogConfirmType.CONFIRM_CANCEL_SHARE_BUDGET_FROM_USER,
          data: {
            message: message
          }
        },
      });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.textNormalBudget = this.normalBudgetBooker ? this.transformBudget(this.normalBudgetBooker) : `${this.translate.transform('budget-limit-unlimited')}`;
        this.shareBudget = undefined;
        this.selectTemplate();

        this.authorizeService.removeCoBookerCookie();
        this.authorizeService.setShareBudgetObs(false);
        this.onShareBudget.emit(undefined);
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/components/budget-limit/budget-limit.component.ts